import { NavLink, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, KanbanSquare, CalendarDays, CalendarCheck, MessageSquareText, Settings, LogOut, Bell, Plus, Sparkles, Wallet, CreditCard, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, useApi, useRefresh } from "@/lib/api";
import { fmtDateTime, urgency } from "@/lib/crm";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ClientFormDialog } from "@/components/ClientFormDialog";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/clienti", label: "Clienti", icon: Users, id: "clienti" },
  { to: "/pratiche", label: "Pratiche", icon: KanbanSquare, id: "pratiche" },
  { to: "/calendario", label: "Richiami", icon: CalendarDays, id: "calendario" },
  { to: "/prenotazioni", label: "Prenota", icon: CalendarCheck, id: "prenotazioni" },
  { to: "/modelli", label: "Modelli", icon: MessageSquareText, id: "modelli" },
  { to: "/assistente", label: "Assistente AI", icon: Sparkles, id: "assistente" },
  { to: "/provvigioni", label: "Provvigioni", icon: Wallet, id: "provvigioni" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, id: "impostazioni" },
];

const NotificationBell = () => {
  const { data = [] } = useApi("/notifications", { refetchInterval: 60000 });
  const { data: fresh = [] } = useApi("/booking/unseen", { refetchInterval: 60000 });
  const invalidate = useRefresh();
  const total = data.length + fresh.length;
  const onOpen = (o) => { if (!o && fresh.length) api.post("/booking/mark-seen").then(invalidate); };
  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100 transition-colors">
          <Bell className="h-5 w-5 text-slate-700" />
          {total > 0 && (
            <span data-testid="notifications-count" className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold grid place-items-center">
              {total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        {fresh.length > 0 && (
          <div data-testid="notifications-bookings">
            <div className="px-4 py-3 border-b font-display font-semibold text-sm text-sky-700 bg-sky-50">Nuove prenotazioni online ({fresh.length})</div>
            {fresh.map((r) => (
              <Link key={r.reminder_id} to={`/clienti/${r.client_id}`} className="flex gap-3 px-4 py-3 hover:bg-slate-50 border-b">
                <CalendarCheck className="h-4 w-4 mt-0.5 text-sky-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    <span className={r.event === "annullato" ? "text-rose-600" : r.event === "spostato" ? "text-amber-600" : "text-sky-700"}>
                      {r.event === "annullato" ? "Annullato" : r.event === "spostato" ? "Spostato" : "Nuovo"}
                    </span>{" "}· {fmtDateTime(r.due_at)}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{r.reason}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
        <div className="px-4 py-3 border-b font-display font-semibold text-sm">Richiami da fare</div>
        <div className="max-h-80 overflow-auto">
          {data.length === 0 && <p className="p-4 text-sm text-slate-500">Tutto in ordine per oggi.</p>}
          {data.map((r) => (
            <Link key={r.reminder_id} to={`/clienti/${r.client_id}`} className="flex gap-3 px-4 py-3 hover:bg-slate-50 border-b last:border-0">
              <span className={`mt-1.5 h-2 w-2 rounded-full ${urgency(r.due_at) === "scaduto" ? "bg-rose-500" : "bg-amber-500"}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{r.client_name}</p>
                <p className="text-xs text-slate-500 truncate">{fmtDateTime(r.due_at)} · {r.reason || "Richiamo"}</p>
              </div>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SideLink = ({ to, label, icon: I, id }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={`nav-${id}`}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-sky-500/15 text-sky-300" : "text-slate-400 hover:text-white hover:bg-white/5"}`
    }
  >
    <I className="h-[18px] w-[18px]" /> {label}
  </NavLink>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [newClient, setNewClient] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50/70">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#0F172A] p-5">
        <div className="flex items-center gap-3 px-2 mb-10">
          <div className="h-10 w-10 rounded-lg bg-white grid place-items-center p-1"><img src="/logo-icon.png" alt="Kreditofacile" data-testid="sidebar-logo" className="h-full w-full object-contain" /></div>
          <span className="font-display text-lg font-bold text-white tracking-tight">Kreditofacile</span>
        </div>
        <nav className="flex flex-col gap-1">
          {NAV.map((n) => <SideLink key={n.to} {...n} />)}
          <SideLink to="/abbonamento" label="Abbonamento" icon={CreditCard} id="abbonamento" />
          {user?.access === "admin" && <SideLink to="/admin" label="Admin" icon={ShieldCheck} id="admin" />}
        </nav>
        <div className="mt-auto flex items-center gap-3 px-2 pt-4 border-t border-slate-800">
          <img src={user?.picture} alt="" className="h-9 w-9 rounded-full bg-slate-700" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }} className="text-slate-500 hover:text-white" title="Esci">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 backdrop-blur-md bg-white/85 border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-5 lg:px-10">
            <span className="lg:hidden flex items-center gap-2 font-display font-bold"><img src="/logo-icon.png" alt="" className="h-7 w-7 object-contain" />Kreditofacile</span>
            <span className="hidden lg:block text-sm text-slate-500 capitalize">
              {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </span>
            <div className="flex items-center gap-2">
              <button data-testid="quick-add-client-btn" onClick={() => setNewClient(true)} className="hidden sm:flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-4 py-2 text-sm font-medium hover:bg-sky-600 transition-colors">
                <Plus className="h-4 w-4" /> Nuovo nominativo
              </button>
              <NotificationBell />
            </div>
          </div>
        </header>
        {user?.access === "readonly" && (
          <div data-testid="readonly-banner" className="bg-amber-50 border-b border-amber-200 px-5 lg:px-10 py-3 text-sm text-amber-800 flex flex-wrap items-center gap-3">
            Abbonamento non attivo: il tuo account è in sola lettura per 15 giorni.
            <Link to="/abbonamento" data-testid="readonly-reactivate" className="font-semibold underline">Riattiva ora</Link>
          </div>
        )}
        <main className="px-5 lg:px-10 py-8 pb-28 lg:pb-10 max-w-[1500px]">{children}</main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 flex overflow-x-auto" data-testid="mobile-nav">
        {NAV.filter((n) => n.id !== "impostazioni").concat(NAV.filter((n) => n.id === "impostazioni"))
          .concat([{ to: "/abbonamento", label: "Abbonamento", icon: CreditCard, id: "abbonamento" }])
          .concat(user?.access === "admin" ? [{ to: "/admin", label: "Admin", icon: ShieldCheck, id: "admin" }] : [])
          .map(({ to, label, icon: I, id }) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`mnav-${id}`} className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 px-1 min-w-[72px] flex-1 shrink-0 text-[10px] leading-tight text-center ${isActive ? "text-sky-600" : "text-slate-500"}`}>
            <I className="h-5 w-5" /> <span className="truncate max-w-[68px]">{label === "Assistente AI" ? "AI" : label}</span>
          </NavLink>
        ))}
      </nav>
      <ClientFormDialog open={newClient} onOpenChange={setNewClient} onSaved={(c) => navigate(`/clienti/${c.client_id}`)} />
    </div>
  );
}
